// Phase 6 — tick controller.
//
// Polls `/api/tick` on an adaptive cadence:
// - 5 s when within 30 s of any task boundary (`startAt` or
//   `mustCompleteBy`);
// - 10 s otherwise.
//
// `document.visibilityState === 'hidden'` ⇒ pause polling; resume
// immediately when visibility returns. Timer interpolation is owned by
// `useElapsedSeconds` and continues regardless.
//
// On every successful response: update `classified`, `nudge`,
// `clockOffset`, `lastServerSeenAt` via the store.
//
// The controller is deliberately a small class so it can be unit-tested
// with fake timers; the React layer wraps it in a hook below.

import type { ScheduleEntry, TickResponse } from '@cookchow/shared';

export interface TickControllerDeps {
  // Fetch `/api/tick`. Injected so tests can drive the controller with
  // a deterministic stub.
  fetchTick: (req: {
    planToken: string;
    completedTaskIds: string[];
    nowClient: string;
  }) => Promise<TickResponse>;
  // Read the current cooking-session inputs each cycle. The controller
  // does not capture these at construction time — `completedTaskIds`
  // changes as the user checks tasks off.
  getInputs: () => {
    planToken: string | null;
    schedule: readonly ScheduleEntry[];
    completedTaskIds: string[];
  } | null;
  // Notify the consumer of a successful response.
  onResponse: (res: TickResponse) => void;
  // Notify the consumer when a tick failed (network or HTTP).
  onError?: (err: unknown) => void;
}

const FAST_CADENCE_MS = 5000;
const SLOW_CADENCE_MS = 10000;
const BOUNDARY_WINDOW_MS = 30000;

export function nearAnyBoundary(
  schedule: readonly ScheduleEntry[],
  nowMs: number,
  windowMs: number = BOUNDARY_WINDOW_MS,
): boolean {
  for (const e of schedule) {
    const startMs = Date.parse(e.startAt);
    const mustMs = Date.parse(e.mustCompleteBy);
    if (Number.isFinite(startMs) && Math.abs(startMs - nowMs) <= windowMs) return true;
    if (Number.isFinite(mustMs) && Math.abs(mustMs - nowMs) <= windowMs) return true;
  }
  return false;
}

export function pickCadenceMs(schedule: readonly ScheduleEntry[], nowMs: number): number {
  return nearAnyBoundary(schedule, nowMs) ? FAST_CADENCE_MS : SLOW_CADENCE_MS;
}

// Minimal subset of the document visibility API used by the controller.
// Injected so the test harness can drive it without touching a real
// document.
export interface VisibilityLike {
  visible: () => boolean;
  onChange: (cb: () => void) => () => void;
}

function browserVisibility(): VisibilityLike {
  return {
    visible: () => (typeof document === 'undefined' ? true : document.visibilityState !== 'hidden'),
    onChange: (cb) => {
      if (typeof document === 'undefined') return () => undefined;
      document.addEventListener('visibilitychange', cb);
      return () => document.removeEventListener('visibilitychange', cb);
    },
  };
}

export class TickController {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private detachVisibility: (() => void) | null = null;
  private stopped = false;

  constructor(
    private readonly deps: TickControllerDeps,
    private readonly visibility: VisibilityLike = browserVisibility(),
  ) {}

  start(): void {
    this.stopped = false;
    this.detachVisibility = this.visibility.onChange(() => {
      if (this.visibility.visible() && !this.stopped) {
        this.scheduleNext(0);
      } else {
        this.clearTimer();
      }
    });
    // First tick — fire immediately on mount.
    this.scheduleNext(0);
  }

  stop(): void {
    this.stopped = true;
    this.clearTimer();
    if (this.detachVisibility) {
      this.detachVisibility();
      this.detachVisibility = null;
    }
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private scheduleNext(delayMs: number): void {
    this.clearTimer();
    if (this.stopped) return;
    if (!this.visibility.visible()) return;
    this.timer = setTimeout(() => {
      void this.tickOnce();
    }, delayMs);
  }

  private async tickOnce(): Promise<void> {
    if (this.stopped || !this.visibility.visible()) return;
    const inputs = this.deps.getInputs();
    if (!inputs || !inputs.planToken) {
      // Nothing to do — try again on the slow cadence so we don't busy-poll
      // before the plan is fully wired.
      this.scheduleNext(SLOW_CADENCE_MS);
      return;
    }
    const nowMs = Date.now();
    try {
      const res = await this.deps.fetchTick({
        planToken: inputs.planToken,
        completedTaskIds: [...inputs.completedTaskIds],
        nowClient: new Date(nowMs).toISOString(),
      });
      this.deps.onResponse(res);
    } catch (err) {
      this.deps.onError?.(err);
    }
    const cadence = pickCadenceMs(inputs.schedule, nowMs);
    this.scheduleNext(cadence);
  }
}

// Offline indicator threshold — chip becomes visible after this long
// without a successful tick. README §9: silent for up to 60 s.
export const OFFLINE_THRESHOLD_MS = 60000;

export function isOffline(
  lastServerSeenAt: number | null,
  nowPerf: number,
  thresholdMs: number = OFFLINE_THRESHOLD_MS,
): boolean {
  if (lastServerSeenAt === null) return false;
  return nowPerf - lastServerSeenAt >= thresholdMs;
}
